'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function HoursCalculator() {
  const t = useTranslations("tools.hoursCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Same Day Calculator State
  const [sameDayInputs, setSameDayInputs] = useState({
    startTime: '8:30',
    startPeriod: 'AM',
    endTime: '5:30',
    endPeriod: 'PM'
  });

  // Two Dates Calculator State
  const [twoDatesInputs, setTwoDatesInputs] = useState({
    startDate: '',
    startTime: '8:30',
    startPeriod: 'AM',
    endDate: '',
    endTime: '5:30',
    endPeriod: 'PM'
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);

    // Initialize dates
    const today = new Date().toISOString().split('T')[0];
    setTwoDatesInputs(prev => ({
      ...prev,
      startDate: today,
      endDate: today
    }));

    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const parseTime = (timeStr: string, period: string) => {
    const parts = timeStr.trim().split(':');
    if (parts.length !== 2) return null;

    let hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);

    if (isNaN(hours) || isNaN(minutes) || hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
      return null;
    }

    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return { hours, minutes };
  };

  const calculateDuration = (startDate: Date, endDate: Date) => {
    const diffMs = endDate.getTime() - startDate.getTime();

    if (diffMs < 0) {
      return null;
    }

    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const decimalHours = (totalMinutes / 60).toFixed(2);

    return {
      hours,
      minutes,
      totalMinutes,
      decimalHours,
      formatted: `${hours} ${t('hours')} ${minutes} ${t('minutes')}`
    };
  };

  const handleSameDayCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const startTime = parseTime(sameDayInputs.startTime, sameDayInputs.startPeriod);
    const endTime = parseTime(sameDayInputs.endTime, sameDayInputs.endPeriod);

    if (!startTime || !endTime) {
      setError(t('errorInvalidTime'));
      setResults(null);
      return;
    }

    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), startTime.hours, startTime.minutes);
    let endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), endTime.hours, endTime.minutes);

    if (endDate <= startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }

    const duration = calculateDuration(startDate, endDate);

    if (duration) {
      setResults(duration);
      setError('');
    } else {
      setError(t('errorEndBeforeStart'));
      setResults(null);
    }
  };

  const handleTwoDatesCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const startTime = parseTime(twoDatesInputs.startTime, twoDatesInputs.startPeriod);
    const endTime = parseTime(twoDatesInputs.endTime, twoDatesInputs.endPeriod);

    if (!startTime || !endTime) {
      setError(t('errorInvalidTime'));
      setResults(null);
      return;
    }

    const startDate = new Date(twoDatesInputs.startDate);
    startDate.setHours(startTime.hours, startTime.minutes, 0, 0);

    const endDate = new Date(twoDatesInputs.endDate);
    endDate.setHours(endTime.hours, endTime.minutes, 0, 0);

    const duration = calculateDuration(startDate, endDate);

    if (duration) {
      setResults(duration);
      setError('');
    } else {
      setError(t('errorEndBeforeStart'));
      setResults(null);
    }
  };

  const setNow = (target: string) => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes();
    const period = hours >= 12 ? 'PM' : 'AM';

    if (hours > 12) hours -= 12;
    if (hours === 0) hours = 12;

    const timeStr = `${hours}:${minutes.toString().padStart(2, '0')}`;
    const today = new Date().toISOString().split('T')[0];

    if (target === 'sameDay-start') {
      setSameDayInputs(prev => ({ ...prev, startTime: timeStr, startPeriod: period }));
    } else if (target === 'sameDay-end') {
      setSameDayInputs(prev => ({ ...prev, endTime: timeStr, endPeriod: period }));
    } else if (target === 'twoDates-start') {
      setTwoDatesInputs(prev => ({ ...prev, startTime: timeStr, startPeriod: period, startDate: today }));
    } else if (target === 'twoDates-end') {
      setTwoDatesInputs(prev => ({ ...prev, endTime: timeStr, endPeriod: period, endDate: today }));
    }
  };

  const swapSameDayTimes = () => {
    setSameDayInputs(prev => ({
      startTime: prev.endTime,
      startPeriod: prev.endPeriod,
      endTime: prev.startTime,
      endPeriod: prev.startPeriod
    }));
  };

  const clearSameDay = () => {
    setSameDayInputs({
      startTime: '8:30',
      startPeriod: 'AM',
      endTime: '5:30',
      endPeriod: 'PM'
    });
    setResults(null);
    setError('');
  };

  const clearTwoDates = () => {
    const today = new Date().toISOString().split('T')[0];
    setTwoDatesInputs({
      startDate: today,
      startTime: '8:30',
      startPeriod: 'AM',
      endDate: today,
      endTime: '5:30',
      endPeriod: 'PM'
    });
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    subtitle: getResponsiveStyle(
      { color: colors.textSecondary, marginTop: '0.5rem', lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem', margin: 0 },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    toolSubtitle: getResponsiveStyle(
      { color: colors.textSecondary, margin: 0 },
      { fontSize: '0.875rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    calculatorCards: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr', gap: '2rem', marginBottom: '2rem' },
      { gridTemplateColumns: '1fr', gap: '2rem', marginBottom: '2rem' }
    ),
    calculatorCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    cardTitle: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 },
      { fontSize: '1.125rem', marginBottom: '1rem' },
      { fontSize: '1.25rem', marginBottom: '1.5rem' },
      { fontSize: '1.25rem', marginBottom: '1.5rem' }
    ),
    formRow: getResponsiveStyle(
      { display: 'grid', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '120px 1fr auto', gap: '1rem' },
      { gridTemplateColumns: '120px 1fr auto', gap: '1rem' }
    ),
    label: getResponsiveStyle(
      { fontWeight: 500, color: colors.textSecondary },
      { fontSize: '0.875rem', marginBottom: '0.25rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    timeInputGroup: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
      flexWrap: 'wrap' as const
    },
    timeInput: getResponsiveStyle(
      {
        flex: 1,
        minWidth: '80px',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        outline: 'none'
      },
      { padding: '0.625rem 0.75rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' }
    ),
    select: getResponsiveStyle(
      {
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        background: 'white',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        outline: 'none'
      },
      { padding: '0.625rem 0.75rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' }
    ),
    dateInput: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        outline: 'none'
      },
      { padding: '0.625rem 0.75rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' }
    ),
    nowBtn: getResponsiveStyle(
      {
        background: '#f1f5f9',
        border: 'none',
        borderRadius: '0.5rem',
        color: '#2563eb',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        fontWeight: 500,
        whiteSpace: 'nowrap' as const
      },
      { padding: '0.5rem 0.75rem', fontSize: '0.875rem' },
      { padding: '0.5rem 1rem', fontSize: '0.875rem' },
      { padding: '0.5rem 1rem', fontSize: '0.875rem' }
    ),
    swapBtn: {
      background: '#f1f5f9',
      border: 'none',
      borderRadius: '0.5rem',
      padding: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.25rem'
    },
    buttonGroup: getResponsiveStyle(
      { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      { flexDirection: 'row' as const }
    ),
    calculateBtn: getResponsiveStyle(
      {
        flex: 1,
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem', minHeight: '44px' },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' }
    ),
    clearBtn: getResponsiveStyle(
      {
        flex: 1,
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: '#f1f5f9',
        color: colors.textSecondary
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem', minHeight: '44px' },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginTop: '1.5rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem', marginTop: '2rem' },
      { padding: '2rem', marginTop: '2rem' }
    ),
    resultItem: getResponsiveStyle(
      {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '0.5rem',
        borderLeft: '4px solid #2563eb'
      },
      { padding: '0.875rem', flexDirection: 'column' as const, gap: '0.5rem', alignItems: 'flex-start' },
      { padding: '1rem', flexDirection: 'row' as const },
      { padding: '1rem', flexDirection: 'row' as const }
    ),
    resultLabel: getResponsiveStyle(
      { fontWeight: 500, color: colors.textSecondary },
      { fontSize: '0.875rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    resultValue: getResponsiveStyle(
      { fontWeight: 700, color: '#2563eb' },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultBreakdown: getResponsiveStyle(
      { background: '#f8fafc', borderRadius: '0.5rem', marginTop: '1rem' },
      { padding: '0.875rem' },
      { padding: '1rem' },
      { padding: '1rem' }
    ),
    breakdownItem: getResponsiveStyle(
      { display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', paddingBottom: '0.5rem', color: colors.textSecondary },
      { fontSize: '0.875rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),
    errorMessage: getResponsiveStyle(
      {
        background: '#fef2f2',
        borderLeft: '4px solid #ef4444',
        borderRadius: '0.5rem',
        color: '#991b1b',
        marginTop: '1rem'
      },
      { padding: '0.875rem', fontSize: '0.875rem' },
      { padding: '1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '1.5rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem', marginBottom: '2rem' },
      { padding: '2.5rem', marginBottom: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    listItem: {
      marginBottom: '0.5rem'
    },
    faqItem: getResponsiveStyle(
      { marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2rem' }
    ),
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' }
    ),
    reference: getResponsiveStyle(
      { marginBottom: '1rem' },
      { fontSize: '0.875rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    link: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.3s ease'
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.subtitle}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t('toolTitle')}</h2>
            <p style={styles.toolSubtitle}>{t('toolSubtitle')}</p>
          </div>

          <div style={styles.calculatorCards}>
            <div style={styles.calculatorCard}>
              <h3 style={styles.cardTitle}>
                <span>📅</span> {t('sameDayTitle')}
              </h3>
              <form onSubmit={handleSameDayCalculate}>
                <div style={styles.formRow}>
                  <label style={styles.label}>{t('startTime')}</label>
                  <div style={styles.timeInputGroup}>
                    <input
                      type="text"
                      style={styles.timeInput}
                      placeholder="8:30"
                      value={sameDayInputs.startTime}
                      onChange={(e) => setSameDayInputs(prev => ({ ...prev, startTime: e.target.value }))}
                      required
                    />
                    <select
                      style={styles.select}
                      value={sameDayInputs.startPeriod}
                      onChange={(e) => setSameDayInputs(prev => ({ ...prev, startPeriod: e.target.value }))}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                    <button type="button" style={styles.nowBtn} onClick={() => setNow('sameDay-start')}>
                      {t('nowButton')}
                    </button>
                  </div>
                  {screenSize !== 'mobile' && (
                    <button type="button" style={styles.swapBtn} onClick={swapSameDayTimes}>
                      ⇅
                    </button>
                  )}
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>{t('endTime')}</label>
                  <div style={styles.timeInputGroup}>
                    <input
                      type="text"
                      style={styles.timeInput}
                      placeholder="5:30"
                      value={sameDayInputs.endTime}
                      onChange={(e) => setSameDayInputs(prev => ({ ...prev, endTime: e.target.value }))}
                      required
                    />
                    <select
                      style={styles.select}
                      value={sameDayInputs.endPeriod}
                      onChange={(e) => setSameDayInputs(prev => ({ ...prev, endPeriod: e.target.value }))}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                    <button type="button" style={styles.nowBtn} onClick={() => setNow('sameDay-end')}>
                      {t('nowButton')}
                    </button>
                  </div>
                  {screenSize !== 'mobile' && <div></div>}
                </div>
                {screenSize === 'mobile' && (
                  <button type="button" style={{...styles.swapBtn, width: '100%', marginBottom: '1rem'}} onClick={swapSameDayTimes}>
                    ⇅ {t('swapTimes')}
                  </button>
                )}
                <div style={styles.buttonGroup}>
                  <button type="submit" style={styles.calculateBtn}>{t('calculateButton')}</button>
                  <button type="button" style={styles.clearBtn} onClick={clearSameDay}>{t('clearButton')}</button>
                </div>
              </form>
            </div>

            <div style={styles.calculatorCard}>
              <h3 style={styles.cardTitle}>
                <span>📆</span> {t('twoDatesTitle')}
              </h3>
              <form onSubmit={handleTwoDatesCalculate}>
                <div style={styles.formRow}>
                  <label style={styles.label}>{t('startDate')}</label>
                  <input
                    type="date"
                    style={styles.dateInput}
                    value={twoDatesInputs.startDate}
                    onChange={(e) => setTwoDatesInputs(prev => ({ ...prev, startDate: e.target.value }))}
                    required
                  />
                  {screenSize !== 'mobile' && <div></div>}
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>{t('startTime')}</label>
                  <div style={styles.timeInputGroup}>
                    <input
                      type="text"
                      style={styles.timeInput}
                      placeholder="8:30"
                      value={twoDatesInputs.startTime}
                      onChange={(e) => setTwoDatesInputs(prev => ({ ...prev, startTime: e.target.value }))}
                      required
                    />
                    <select
                      style={styles.select}
                      value={twoDatesInputs.startPeriod}
                      onChange={(e) => setTwoDatesInputs(prev => ({ ...prev, startPeriod: e.target.value }))}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                    <button type="button" style={styles.nowBtn} onClick={() => setNow('twoDates-start')}>
                      {t('nowButton')}
                    </button>
                  </div>
                  {screenSize !== 'mobile' && <div></div>}
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>{t('endDate')}</label>
                  <input
                    type="date"
                    style={styles.dateInput}
                    value={twoDatesInputs.endDate}
                    onChange={(e) => setTwoDatesInputs(prev => ({ ...prev, endDate: e.target.value }))}
                    required
                  />
                  {screenSize !== 'mobile' && <div></div>}
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>{t('endTime')}</label>
                  <div style={styles.timeInputGroup}>
                    <input
                      type="text"
                      style={styles.timeInput}
                      placeholder="5:30"
                      value={twoDatesInputs.endTime}
                      onChange={(e) => setTwoDatesInputs(prev => ({ ...prev, endTime: e.target.value }))}
                      required
                    />
                    <select
                      style={styles.select}
                      value={twoDatesInputs.endPeriod}
                      onChange={(e) => setTwoDatesInputs(prev => ({ ...prev, endPeriod: e.target.value }))}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                    <button type="button" style={styles.nowBtn} onClick={() => setNow('twoDates-end')}>
                      {t('nowButton')}
                    </button>
                  </div>
                  {screenSize !== 'mobile' && <div></div>}
                </div>
                <div style={styles.buttonGroup}>
                  <button type="submit" style={styles.calculateBtn}>{t('calculateButton')}</button>
                  <button type="button" style={styles.clearBtn} onClick={clearTwoDates}>{t('clearButton')}</button>
                </div>
              </form>
            </div>
          </div>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.h2}>⏱️ {t('resultsTitle')}</h3>
              <div>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('totalDuration')}</span>
                  <span style={styles.resultValue}>{results.formatted}</span>
                </div>
              </div>
              <div style={styles.resultBreakdown}>
                <div style={styles.breakdownItem}>
                  <span>{t('totalHours')}</span>
                  <strong>{results.hours}</strong>
                </div>
                <div style={styles.breakdownItem}>
                  <span>{t('totalMinutes')}</span>
                  <strong>{results.totalMinutes}</strong>
                </div>
                <div style={styles.breakdownItem}>
                  <span>{t('decimalHours')}</span>
                  <strong>{results.decimalHours}</strong>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div style={styles.errorMessage}>
              {error}
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToStep1')}</li>
          <li style={styles.listItem}>{t('howToStep2')}</li>
          <li style={styles.listItem}>{t('howToStep3')}</li>
          <li style={styles.listItem}>{t('howToStep4')}</li>
          <li style={styles.listItem}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsParagraph1')}</p>
        <p style={styles.paragraph}>{t('insightsParagraph2')}</p>
        <p style={styles.paragraph}>{t('insightsParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('benefitsTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('benefit1')}</li>
          <li style={styles.listItem}>{t('benefit2')}</li>
          <li style={styles.listItem}>{t('benefit3')}</li>
          <li style={styles.listItem}>{t('benefit4')}</li>
          <li style={styles.listItem}>{t('benefit5')}</li>
        </ul>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t('featuresTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('feature1')}</li>
          <li style={styles.listItem}>{t('feature2')}</li>
          <li style={styles.listItem}>{t('feature3')}</li>
          <li style={styles.listItem}>{t('feature4')}</li>
          <li style={styles.listItem}>{t('feature5')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('bestPracticesTitle')}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('practice1')}</li>
          <li style={styles.listItem}>{t('practice2')}</li>
          <li style={styles.listItem}>{t('practice3')}</li>
          <li style={styles.listItem}>{t('practice4')}</li>
          <li style={styles.listItem}>{t('practice5')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.paragraph}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.paragraph}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.paragraph}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.paragraph}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.paragraph}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <div style={styles.reference}>
          <a href="https://www.kumospace.com/blog/time-clock-calculator-work-hours" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('reference1')}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://empmonitor.com/blog/time-clock-calculator-in-your-business/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('reference2')}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://desktrack.timentask.com/blog/time-card-calculator/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('reference3')}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.beforesunset.ai/post/why-should-you-use-a-time-card-calculator" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('reference4')}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.timerewards.com/time-card-calculator/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t('reference5')}
          </a>
        </div>
      </section>
    </div>
  );
}